import dayjs from "dayjs";

type TaxEvent = Record<string, any> & {
  buy_txn?: any;
  sell_txn?: any;
  capital_term_name?: string;
  asset?: string;
  asset_id?: number;
  quantity?: number;
  proceeds?: number;
  cost?: number;
  gain?: number;
};

type IncomeEvent = Record<string, any> & {
  sell_txn?: any;
  asset?: string;
  quantity?: number;
  gain?: number;
};

export type CapitalGainEventRow = {
  id: number | string | null;
  date: string;
  acquisitionDate: string | null;
  type: string | null;
  linkedId: number | string | null;
  inflowQuantity: number | null;
  inflowAsset: string | null;
  outflowQuantity: number | null;
  outflowAsset: string | null;
  fee: number;
  acquisitionCost: number;
  salesProceeds: number;
  capitalGain: number;
  holdingPeriod: number | null;
  taxRelevant: boolean;
  disposalType: string | null;
  costAveragePrice: number | null;
  exchange: string | null;
  priceIsApproximate: boolean;
  priceIsStale: boolean;
};

export type CapitalGainGroup = {
  asset: string;
  dataSource: string;
  events: CapitalGainEventRow[];
};

export type IncomeRow = {
  id: number | string | null;
  date: string;
  type: string | null;
  dataSource: string;
  quantity: number | null;
  asset: string | null;
  contractAddress: string | null;
  valueOrGainLoss: number;
};

export type LotTrackingRow = {
  id: number | string | null;
  date: string;
  type: string | null;
  asset: string | null;
  quantity: number | null;
  acquisitionCost: number;
  exchange: string | null;
  consumedByIds: (number | string)[];
  consumedByDates: string[];
  fullyConsumed: boolean;
};

const formatDate = (ms?: number) =>
  ms ? dayjs(ms).format("DD/MM/YYYY HH:mm:ss") : "";

const parseFiledPdfMeta = (
  priceSource: string | null | undefined
): Record<string, string> => {
  if (!priceSource?.startsWith("filed_pdf|")) return {};
  return priceSource
    .split("|")
    .slice(1)
    .reduce<Record<string, string>>((acc, part) => {
      const separatorIndex = part.indexOf("=");
      if (separatorIndex === -1) return acc;
      acc[part.slice(0, separatorIndex)] = part.slice(separatorIndex + 1);
      return acc;
    }, {});
};

const filedPdfSortTimestamp = (priceSource: string | null | undefined) => {
  const disposed = parseFiledPdfMeta(priceSource).disp;
  if (!disposed) return 0;
  const parsed = new Date(disposed).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const reindexAndNormalize = (
  taxEvents: TaxEvent[] = [],
  incomeEvents: IncomeEvent[] = []
) => {
  const safeTaxEvents = Array.isArray(taxEvents) ? taxEvents : [];
  const safeIncomeEvents = Array.isArray(incomeEvents) ? incomeEvents : [];

  const taxableEvents = safeTaxEvents.filter((event) => event?.sell_txn);
  const filedPdfTaxEvents = safeTaxEvents.filter(
    (event) => !event?.sell_txn && event?.price_source?.startsWith("filed_pdf|")
  );
  // A buy_txn is required to render a LOT row, never to render a DISPOSAL row:
  // a Section 104 disposal drawn from the pool can carry buy_txn_id null (no
  // single matched acquisition). Filtering disposals on buy_txn dropped those
  // from the per-asset workings while the server summary still counted them, so
  // the Computation's workings and SA108 box 13.1 disagreed.
  const taxEventsWithBuyId = taxableEvents.filter(
    (event) => event?.buy_txn?.id
  );

  const allEvents = [
    ...taxableEvents.map((e) => ({
      ...e,
      __sortTimestamp: e.sell_txn?.timestamp ?? 0,
      __type: "tax" as const,
    })),
    ...filedPdfTaxEvents.map((e) => ({
      ...e,
      __sortTimestamp: filedPdfSortTimestamp(e.price_source),
      __type: "tax" as const,
    })),
    ...Array.from(
      new Map(
        taxEventsWithBuyId.map((e) => [
          e.buy_txn.id,
          {
            ...e,
            __sortTimestamp: e.buy_txn?.timestamp ?? 0,
            __type: "buy" as const,
          },
        ])
      ).values()
    ),
    ...safeIncomeEvents
      .filter((event) => event?.sell_txn)
      .map((e) => ({
        ...e,
        __sortTimestamp: e.sell_txn?.timestamp ?? 0,
        __type: "income" as const,
      })),
  ];

  allEvents.sort(
    (a, b) =>
      new Date(a.__sortTimestamp || 0).getTime() -
      new Date(b.__sortTimestamp || 0).getTime()
  );

  let counter = 1;
  const idMap = new Map<string, number>();

  const normalized = allEvents.map((event: any) => {
    const normalizeTxn = (txn: any) => {
      if (!txn?.id) {
        return txn;
      }
      if (!idMap.has(txn.id)) {
        idMap.set(txn.id, counter++);
      }
      return { ...txn, id: idMap.get(txn.id) };
    };

    return {
      ...event,
      buy_txn: event.buy_txn ? normalizeTxn(event.buy_txn) : null,
      sell_txn: event.sell_txn ? normalizeTxn(event.sell_txn) : null,
    };
  });

  return {
    taxEvents: normalized.filter((e: any) => e.__type !== "income"),
    incomeEvents: normalized
      .filter((e: any) => e.__type === "income")
      .map(({ __type, ...rest }: any) => rest),
  };
};

const buildCapitalGain = (
  taxEvents: any[],
  baseCurrency?: string
): CapitalGainGroup[] => {
  // Only include disposal events (not buy rows)
  const disposalEvents = taxEvents.filter((e: any) => e.__type === "tax");

  // Nullish, not `|| 0`: a genuinely-unmapped disposal (asset_id null) must
  // not merge into the placeholder DEFAULT coin (id 0). Keep this key in sync
  // with the server grouping in exports.ts exportUkCapitalGainsComputationHtml
  // so the Computation report and SA108 PDF form identical asset sections.
  //
  // Display-level mirror of the engine's constrained canonicalization: when a
  // ticker's mapped events all share exactly ONE real asset_id, fold its
  // unmapped/placeholder (null / 0) events into that same section — they are
  // the same asset with inconsistent mapping, and two same-name sections read
  // as split pooling to an accountant. Ambiguous tickers keep distinct
  // sections.
  const realIdsByAsset: Record<string, Set<number>> = {};
  for (const evt of disposalEvents) {
    if (evt.asset_id !== null && evt.asset_id !== undefined && evt.asset_id !== 0) {
      (realIdsByAsset[evt.asset] ??= new Set()).add(evt.asset_id);
    }
  }
  const grouped: Record<string, any[]> = {};
  for (const evt of disposalEvents) {
    const asset = evt.asset;
    let assetId: number | string = evt.asset_id ?? "unmapped";
    if (assetId === "unmapped" || assetId === 0) {
      const realIds = realIdsByAsset[asset];
      if (realIds?.size === 1) {
        assetId = Array.from(realIds)[0];
      } else if (!realIds || realIds.size === 0) {
        // no real id anywhere for this ticker: 0 (DEFAULT placeholder) and
        // null both just mean "no mapping" — one section, not two
        assetId = "unmapped";
      }
    }
    const key = `${asset}-${assetId}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(evt);
  }

  return Object.values(grouped).map((events) => {
    const sample = events[0];
    // S104 pooling is asset-wide across every wallet/exchange, so the group can
    // span multiple accounts. Label it as such instead of showing whichever
    // exchange happens to hold the first event (which reads as per-wallet
    // pooling to an accountant).
    const exchanges = Array.from(
      new Set(
        events
          .map(
            (e: any) =>
              e.sell_txn?.exchange || parseFiledPdfMeta(e.price_source).ex
          )
          .filter(Boolean)
      )
    );
    const dataSource =
      exchanges.length > 1
        ? `Multiple accounts (${exchanges.length})`
        : exchanges[0] || "";
    return {
      asset: sample.asset,
      dataSource,
      events: events.map((e: any) => {
        const filedPdfMeta = parseFiledPdfMeta(e.price_source);
        const ratio =
          e.quantity && e.sell_txn?.sellqty ? e.quantity / e.sell_txn.sellqty : 1;
        const fee =
          e.fees != null
            ? e.fees
            : (e.sell_txn?.feevalue ||
                (baseCurrency && e.sell_txn?.feeasset === baseCurrency
                  ? e.sell_txn?.feeqty
                  : 0)) * ratio;
        const salesProceeds =
          e.proceeds != null
            ? e.proceeds
            : (e.sell_txn?.total || 0) * ratio;
        const acquisitionCost =
          e.cost != null ? e.cost : (e.buy_txn?.total || 0);

        return {
          id: e.sell_txn?.id ?? filedPdfMeta.id ?? null,
          date: e.sell_txn?.timestamp ? formatDate(e.sell_txn.timestamp) : filedPdfMeta.disp || "",
          acquisitionDate: e.buy_txn?.timestamp ? formatDate(e.buy_txn.timestamp) : filedPdfMeta.acq || null,
          type: e.sell_txn?.side || e.sell_txn?.type || (filedPdfMeta.id ? "disposal" : null),
          linkedId: e.buy_txn?.id ?? filedPdfMeta.lot ?? null,
          inflowQuantity: (e.sell_txn?.buyqty || 0) * ratio,
          inflowAsset: e.sell_txn?.buyasset || null,
          outflowQuantity: e.quantity != null
            ? e.quantity
            : (e.sell_txn?.sellqty || 0) * ratio,
          outflowAsset: e.sell_txn?.sellasset || e.asset || null,
          fee,
          acquisitionCost,
          salesProceeds,
          capitalGain: e.gain || 0,
          holdingPeriod:
            e.sell_txn?.timestamp && e.buy_txn?.timestamp
              ? Math.floor(
                  (e.sell_txn.timestamp - e.buy_txn.timestamp) /
                    (1000 * 60 * 60 * 24)
                )
              : null,
          taxRelevant: e?.is_taxable ?? true,
          disposalType: e.disposal_type || null,
          costAveragePrice: e.cost_average_price ?? null,
          exchange: e.sell_txn?.exchange || filedPdfMeta.ex || null,
          priceIsApproximate: !!e.price_is_approximate,
          priceIsStale: !!e.price_is_stale,
        } as CapitalGainEventRow;
      }),
    } as CapitalGainGroup;
  });
};

const buildIncome = (incomeEvents: any[]): IncomeRow[] =>
  incomeEvents.map((e: any) => ({
    id: e.sell_txn?.id ?? null,
    date: formatDate(e.sell_txn?.timestamp),
    type: e.sell_txn?.side || e.sell_txn?.type || null,
    dataSource: e.sell_txn?.datasource || e.exchange || "-",
    quantity: e.quantity ?? null,
    asset: e.asset ?? null,
    contractAddress: e.sell_txn?.contractAddress || null,
    valueOrGainLoss: e.gain || 0,
  }));

const buildLotTracking = (taxEvents: any[]): LotTrackingRow[] => {
  // Map buy_txn.id -> list of disposal sell_txn.ids that consumed it
  const buyToDisposals = new Map<
    string | number,
    { sellIds: (number | string)[]; sellDates: string[]; buyEvent: any }
  >();

  // Process all tax events (disposals) to build the mapping
  const disposalEvents = taxEvents.filter((e: any) => e.__type === "tax");
  for (const evt of disposalEvents) {
    const buyId = evt.buy_txn?.id;
    const sellId = evt.sell_txn?.id;
    if (!buyId) continue;

    if (!buyToDisposals.has(buyId)) {
      buyToDisposals.set(buyId, {
        sellIds: [],
        sellDates: [],
        buyEvent: evt,
      });
    }
    const entry = buyToDisposals.get(buyId)!;
    if (sellId && !entry.sellIds.includes(sellId)) {
      entry.sellIds.push(sellId);
      entry.sellDates.push(formatDate(evt.sell_txn?.timestamp));
    }
  }

  // Also include buy rows that weren't consumed (unconsumed lots)
  const buyEvents = taxEvents.filter((e: any) => e.__type === "buy");
  for (const evt of buyEvents) {
    const buyId = evt.buy_txn?.id;
    if (buyId && !buyToDisposals.has(buyId)) {
      buyToDisposals.set(buyId, {
        sellIds: [],
        sellDates: [],
        buyEvent: evt,
      });
    }
  }

  // Convert to rows and sort by date
  const rows: LotTrackingRow[] = [];
  for (const [buyId, { sellIds, sellDates, buyEvent }] of buyToDisposals) {
    rows.push({
      id: buyId,
      date: formatDate(buyEvent.buy_txn?.timestamp),
      type: buyEvent.buy_txn?.side || buyEvent.buy_txn?.type || "Buy",
      asset: buyEvent.asset ?? buyEvent.buy_txn?.buyasset ?? null,
      quantity: buyEvent.buy_txn?.buyqty ?? null,
      acquisitionCost: buyEvent.buy_txn?.total ?? 0,
      exchange: buyEvent.buy_txn?.exchange ?? null,
      consumedByIds: sellIds,
      consumedByDates: sellDates,
      fullyConsumed: sellIds.length > 0,
    });
  }

  // Sort by acquisition date
  rows.sort((a, b) => {
    const dateA = a.date ? new Date(a.date.split(" ")[0].split("/").reverse().join("-")).getTime() : 0;
    const dateB = b.date ? new Date(b.date.split(" ")[0].split("/").reverse().join("-")).getTime() : 0;
    return dateA - dateB;
  });

  return rows;
};

export const buildTaxReportTables = (
  taxEvents: TaxEvent[] = [],
  incomeEvents: IncomeEvent[] = [],
  baseCurrency?: string
) => {
  const { taxEvents: normalizedTaxEvents, incomeEvents: normalizedIncomeEvents } =
    reindexAndNormalize(taxEvents, incomeEvents);

  return {
    capitalGainGroups: buildCapitalGain(normalizedTaxEvents, baseCurrency),
    incomeRows: buildIncome(normalizedIncomeEvents),
    lotTrackingRows: buildLotTracking(normalizedTaxEvents),
  };
};
